import { AuthUserPermission } from '@cibo/core'
import { PaginatedFieldsContext } from '@cibo/landmanager'
import { Can, useAuth, usePermissions } from '@cibo/profile'
import { usePreflightCheck, useUpdateWorkflow, useWorkflowManagement } from '@cibo/programs'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { Dispatch, SetStateAction, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { ROUTE } from 'navigation/types'
import { WorkflowActionBarProps } from './WorkflowActionTypes'

export const WorkflowCommitButton = ({
  workflow,
  onCommit,
  setViewingSummary,
  setActionBarError,
}: Pick<WorkflowActionBarProps, 'workflow' | 'onCommit' | 'setViewingSummary'> & {
  setActionBarError: Dispatch<SetStateAction<string | undefined>>
}) => {
  const { t } = useTranslation('impact/features/Workflows')
  const navigate = useNavigate()
  const { mutateAsync: updateWorkflow } = useUpdateWorkflow()
  const { can } = usePermissions()

  const { enqueueSnackbar } = useSnackbar()
  const { persona, userId } = useAuth()
  const { setMessageUserDialog } = useWorkflowManagement()
  const { selectedRowIds } = useContext(PaginatedFieldsContext)

  const {
    mutateAsync: preflightCheck,
    isLoading: loadingPreflightCheck,
    error: preflightError,
  } = usePreflightCheck()

  useEffect(() => {
    preflightCheck({
      programId: workflow.programId,
      resourceIds: selectedRowIds as string[],
    })
  }, [selectedRowIds])

  const { benchmark, fieldCounts, ownedBy } = workflow

  const onCreateEnrollment = () => {
    updateWorkflow({
      workflowId: workflow?.id,
      updates: {
        // @ts-ignore this typing isn't coming through for some reason
        action: 'replace',
        fields: selectedRowIds as string[],
        benchmark: 'enrollment',
      },
    })
      .catch(({ response }) => setActionBarError(response.data.message))
      .finally(() => {
        enqueueSnackbar(t('enrollmentCreated'), {
          variant: 'success',
        })
        setViewingSummary(false)
      })
  }

  const disableProceedButton = selectedRowIds.length <= 0 || !!preflightError

  return fieldCounts.pass > 0 &&
    (ownedBy.userId === userId || can({ useAny: AuthUserPermission.COMMIT_NON_CUSTODIAL })) ? (
    <LoadingButton
      loading={loadingPreflightCheck}
      id="hat2"
      variant="contained"
      disabled={disableProceedButton}
      onClick={() => (benchmark === 'prequalify' ? onCreateEnrollment() : onCommit())}
      data-testid="prequalify_select_next"
    >
      {t('summaryNext', { context: benchmark })}
    </LoadingButton>
  ) : (
    <Can useAny={AuthUserPermission.COMMIT_NON_CUSTODIAL_MESSAGE}>
      {persona !== 'grower' && workflow.benchmark === 'enrollment' && (
        <LoadingButton
          loading={loadingPreflightCheck}
          disabled={disableProceedButton}
          onClick={() =>
            setMessageUserDialog({
              open: true,
              workflow,
              navigateCallback: (programId?: string) =>
                navigate(generatePath(ROUTE.PROGRAM, { programId })),
            })
          }
          variant="contained"
          color="secondary"
          data-testid="workflow-notify-participant"
        >
          {t('messageUser')}
        </LoadingButton>
      )}
    </Can>
  )
}
